import React, { useState, useEffect } from 'react';
import { FaMapMarkerAlt, FaUtensils, FaHiking, FaLandmark, FaMoneyBillWave, FaUsers, FaUser, FaLeaf, FaShieldAlt, FaLightbulb, FaCalendarAlt, FaArrowRight, FaBook, FaGlobe } from 'react-icons/fa';
import SEO from './SEO';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Category definitions with icons and descriptions
const CATEGORIES = [
  { id: 'Destination Guide', name: 'Destination Guides', icon: FaMapMarkerAlt, color: 'blue', description: 'In-depth guides for cities and countries worldwide' },
  { id: 'Food Travel', name: 'Food & Culinary', icon: FaUtensils, color: 'orange', description: 'Local cuisines, restaurants, and food experiences' },
  { id: 'Adventure', name: 'Adventure & Outdoors', icon: FaHiking, color: 'green', description: 'Hiking, activities, and outdoor experiences' },
  { id: 'Culture', name: 'Culture & History', icon: FaLandmark, color: 'purple', description: 'Museums, heritage sites, and local traditions' },
  { id: 'Budget', name: 'Budget Travel', icon: FaMoneyBillWave, color: 'yellow', description: 'Tips for traveling on a shoestring' },
  { id: 'Family Travel', name: 'Family Travel', icon: FaUsers, color: 'pink', description: 'Family-friendly destinations and activities' },
  { id: 'Solo Travel', name: 'Solo Travel', icon: FaUser, color: 'indigo', description: 'Tips and destinations for solo adventurers' },
  { id: 'Sustainable', name: 'Sustainable Travel', icon: FaLeaf, color: 'teal', description: 'Eco-friendly and responsible travel tips' },
  { id: 'Safety', name: 'Travel Safety', icon: FaShieldAlt, color: 'red', description: 'Stay safe while exploring the world' },
  { id: 'Travel Tips', name: 'Travel Tips', icon: FaLightbulb, color: 'amber', description: 'Practical advice for better trips' },
  { id: 'Planning', name: 'Trip Planning', icon: FaCalendarAlt, color: 'cyan', description: 'How to plan your perfect itinerary' },
];

const colorClasses = {
  blue: 'bg-blue-100 text-blue-600 hover:bg-blue-200',
  orange: 'bg-orange-100 text-orange-600 hover:bg-orange-200',
  green: 'bg-green-100 text-green-600 hover:bg-green-200',
  purple: 'bg-purple-100 text-purple-600 hover:bg-purple-200',
  yellow: 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200',
  pink: 'bg-pink-100 text-pink-600 hover:bg-pink-200',
  indigo: 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200',
  teal: 'bg-teal-100 text-teal-600 hover:bg-teal-200',
  red: 'bg-red-100 text-red-600 hover:bg-red-200',
  amber: 'bg-amber-100 text-amber-600 hover:bg-amber-200',
  cyan: 'bg-cyan-100 text-cyan-600 hover:bg-cyan-200',
};

function TravelGuides({ onBack, onViewArticle, onViewDestinations, onGetStarted }) {
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [categoryArticles, setCategoryArticles] = useState({});
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalArticles: 0, destinations: 0 });

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      // Fetch featured/latest articles
      const response = await fetch(`${API_BASE}/api/blog/articles?limit=6&status=published`);
      if (response.ok) {
        const data = await response.json();
        setFeaturedArticles(data.articles || []);
        setStats({
          totalArticles: data.total || 0,
          destinations: data.destinations_count || 50
        });
      }

      // Fetch articles by category
      const categoryData = {};
      for (const cat of CATEGORIES.slice(0, 6)) {
        const catResponse = await fetch(`${API_BASE}/api/blog/articles?category=${encodeURIComponent(cat.id)}&limit=3&status=published`);
        if (catResponse.ok) {
          const catData = await catResponse.json();
          categoryData[cat.id] = catData.articles || [];
        }
      }
      setCategoryArticles(categoryData);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleArticleClick = (slug) => {
    if (onViewArticle) {
      onViewArticle(slug);
    } else {
      window.location.href = `/blog/${slug}`;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-white">
      <SEO
        title="Travel Guides & Tips | ItinerarySeek"
        description="Comprehensive travel guides, destination tips, and expert advice for planning your perfect trip. Explore our collection of travel resources."
        canonical="https://itineraryseek.com/guides"
      />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-600 to-secondary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center justify-between">
            <div>
              {onBack && (
                <button
                  onClick={onBack}
                  className="mb-4 text-white/80 hover:text-white flex items-center gap-2"
                >
                  <span>&larr;</span> Back
                </button>
              )}
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Travel Guides & Resources</h1>
              <p className="text-xl text-white/90 max-w-2xl">
                Expert travel advice, destination guides, and insider tips to help you plan unforgettable adventures.
              </p>
              <div className="flex gap-6 mt-6">
                <div className="text-center">
                  <div className="text-3xl font-bold">{stats.totalArticles}+</div>
                  <div className="text-white/80">Articles</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold">{stats.destinations}+</div>
                  <div className="text-white/80">Destinations</div>
                </div>
              </div>
            </div>
            <div className="hidden lg:block">
              <FaBook className="text-9xl text-white/20" />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Category Grid */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-neutral-800 mb-6">Browse by Category</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {CATEGORIES.map((category) => {
              const Icon = category.icon;
              return (
                <a
                  key={category.id}
                  href={`/blog?category=${encodeURIComponent(category.id)}`}
                  className={`p-4 rounded-xl ${colorClasses[category.color]} transition-all duration-200 group`}
                >
                  <Icon className="text-2xl mb-2" />
                  <h3 className="font-semibold text-sm">{category.name}</h3>
                  <p className="text-xs opacity-75 mt-1 line-clamp-2">{category.description}</p>
                </a>
              );
            })}
          </div>
        </section>

        {/* Featured Articles */}
        <section className="mb-16">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-neutral-800">Latest Travel Guides</h2>
            <a
              href="/blog"
              className="text-primary-600 hover:text-primary-700 font-medium flex items-center gap-2"
            >
              View All <FaArrowRight />
            </a>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-xl shadow-sm p-4 animate-pulse">
                  <div className="bg-neutral-200 h-48 rounded-lg mb-4"></div>
                  <div className="bg-neutral-200 h-4 rounded w-3/4 mb-2"></div>
                  <div className="bg-neutral-200 h-4 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredArticles.map((article) => (
                <article
                  key={article.id}
                  onClick={() => handleArticleClick(article.slug)}
                  className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                >
                  {article.featured_image && (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={article.featured_image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <div className="p-5">
                    {article.category && (
                      <span className="text-xs font-semibold text-primary-600 uppercase tracking-wide">
                        {article.category}
                      </span>
                    )}
                    <h3 className="font-bold text-lg text-neutral-800 mt-1 mb-2 line-clamp-2 group-hover:text-primary-600">
                      {article.title}
                    </h3>
                    <p className="text-neutral-600 text-sm line-clamp-2">
                      {article.excerpt}
                    </p>
                    {article.destination && (
                      <div className="flex items-center gap-1 mt-3 text-sm text-neutral-500">
                        <FaMapMarkerAlt className="text-primary-500" />
                        {article.destination}
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        {/* Category Sections */}
        {CATEGORIES.slice(0, 4).map((category) => {
          const articles = categoryArticles[category.id] || [];
          if (articles.length === 0) return null;

          const Icon = category.icon;
          return (
            <section key={category.id} className="mb-12">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${colorClasses[category.color]}`}>
                    <Icon className="text-xl" />
                  </div>
                  <h2 className="text-xl font-bold text-neutral-800">{category.name}</h2>
                </div>
                <a
                  href={`/blog?category=${encodeURIComponent(category.id)}`}
                  className="text-primary-600 hover:text-primary-700 font-medium text-sm flex items-center gap-1"
                >
                  View All <FaArrowRight />
                </a>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {articles.map((article) => (
                  <article
                    key={article.id}
                    onClick={() => handleArticleClick(article.slug)}
                    className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer group"
                  >
                    <h3 className="font-semibold text-neutral-800 line-clamp-2 group-hover:text-primary-600">
                      {article.title}
                    </h3>
                    <p className="text-neutral-500 text-sm mt-2 line-clamp-2">
                      {article.excerpt}
                    </p>
                  </article>
                ))}
              </div>
            </section>
          );
        })}

        {/* Destinations CTA */}
        <section className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl p-8 text-white mb-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="text-2xl font-bold mb-2">Explore Destinations</h2>
              <p className="text-white/90">
                Browse our collection of destination guides covering {stats.destinations}+ places worldwide.
              </p>
            </div>
            <div className="flex gap-4">
              {onViewDestinations ? (
                <button
                  onClick={onViewDestinations}
                  className="bg-white text-primary-600 px-6 py-3 rounded-lg font-semibold hover:bg-neutral-100 transition-colors flex items-center gap-2"
                >
                  <FaGlobe /> View Destinations
                </button>
              ) : (
                <a
                  href="/destinations"
                  className="bg-white text-primary-600 px-6 py-3 rounded-lg font-semibold hover:bg-neutral-100 transition-colors flex items-center gap-2"
                >
                  <FaGlobe /> View Destinations
                </a>
              )}
            </div>
          </div>
        </section>

        {/* Start Planning CTA */}
        <section className="text-center py-12 border-t border-neutral-200">
          <h2 className="text-2xl font-bold text-neutral-800 mb-4">Ready to Plan Your Trip?</h2>
          <p className="text-neutral-600 mb-6 max-w-xl mx-auto">
            Use our AI-powered itinerary planner to create your perfect travel plan in minutes.
          </p>
          {onGetStarted ? (
            <button
              onClick={onGetStarted}
              className="bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
            >
              Create Free Itinerary
            </button>
          ) : (
            <a
              href="/register"
              className="inline-block bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
            >
              Create Free Itinerary
            </a>
          )}
        </section>
      </div>
    </div>
  );
}

export default TravelGuides;
